function normalizeDealId(row) {
  return String(row?.id ?? row?.deallens_id ?? "")
    .trim()
    .replace(/^deallens#/i, "");
}

function dealDateMs(deal) {
  const raw = deal?.last_modified || deal?.insert_date || null;
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

/**
 * @param {object | null | undefined} cachedTail
 * @param {object | null | undefined} resolvedDeal
 * @returns {boolean}
 */
function isTailAdvancedOver(cachedTail, resolvedDeal) {
  if (!cachedTail) return false;
  const cachedId = normalizeDealId(cachedTail);
  if (cachedId.startsWith("optimistic:")) return true;

  const resolvedId = normalizeDealId(resolvedDeal);
  if (!cachedId || !resolvedId) return false;

  if (cachedId !== resolvedId) {
    return dealDateMs(cachedTail) > dealDateMs(resolvedDeal);
  }

  // Hub mirrors keep the share-row id while status/amount/collection advance.
  if (dealDateMs(cachedTail) > dealDateMs(resolvedDeal)) return true;
  const cachedStatus = String(cachedTail?.status ?? "")
    .toUpperCase()
    .trim();
  const resolvedStatus = String(resolvedDeal?.status ?? "")
    .toUpperCase()
    .trim();
  if (cachedStatus && resolvedStatus && cachedStatus !== resolvedStatus) return true;

  const cachedCollection = String(cachedTail?.collection_id ?? "").trim();
  const resolvedCollection = String(resolvedDeal?.collection_id ?? "").trim();
  return Boolean(cachedCollection && cachedCollection !== resolvedCollection);
}

/**
 * @param {{ tail?: object, pending?: boolean, detailFetchedAt?: number|null } | null | undefined} entry
 * @param {object | null | undefined} resolvedDeal
 * @returns {boolean}
 */
function isAuthoritativeTailEntry(entry, resolvedDeal) {
  if (!entry?.tail) return false;
  if (entry.pending) return true;
  if (isTailAdvancedOver(entry.tail, resolvedDeal)) return true;
  if (entry.detailFetchedAt) {
    if (!resolvedDeal) return true;
    if (dealDateMs(entry.tail) >= dealDateMs(resolvedDeal)) return true;
  }
  return false;
}

/**
 * Unified DealLens detail tip: pending collection tail wins, then pending base tail,
 * then authoritative/advanced tails, then newest by timestamp.
 *
 * @param {object} params
 * @param {object | null | undefined} params.deallensStore
 * @param {object | null | undefined} params.resolvedDeal
 * @param {string | undefined} params.rawUrlParam
 * @param {string | undefined} params.routeSenderId
 * @param {string | undefined} params.routeReceiverId
 * @param {{ collectionScoped?: boolean, collectionId?: string }} [params.tailScope]
 * @param {boolean} [params.routeParamError]
 * @returns {object | null | undefined}
 */
export function resolveEffectiveDealFromThreadCache({
  deallensStore,
  resolvedDeal,
  rawUrlParam,
  routeSenderId,
  routeReceiverId,
  tailScope = {},
  routeParamError = false,
}) {
  if (routeParamError) return resolvedDeal;
  if (!deallensStore) return resolvedDeal ?? null;

  void deallensStore.tailRevision;

  const baseEntry =
    deallensStore.getThreadTailEntryForRoute?.(
      resolvedDeal,
      rawUrlParam,
      routeSenderId,
      routeReceiverId,
    ) ?? null;

  const scopeCollectionId =
    String(tailScope.collectionId ?? resolvedDeal?.collection_id ?? "").trim();
  const collectionScoped = Boolean(tailScope.collectionScoped && scopeCollectionId);

  const collectionEntry = collectionScoped
    ? deallensStore.getThreadTailEntryForRoute?.(
        resolvedDeal,
        rawUrlParam,
        routeSenderId,
        routeReceiverId,
        { collectionScoped: true, collectionId: scopeCollectionId },
      ) ?? null
    : null;

  if (collectionEntry?.pending && collectionEntry.tail) return collectionEntry.tail;
  if (!collectionScoped && baseEntry?.pending && baseEntry.tail) return baseEntry.tail;

  if (collectionEntry && isAuthoritativeTailEntry(collectionEntry, resolvedDeal)) {
    return collectionEntry.tail;
  }

  if (!collectionScoped && baseEntry && isAuthoritativeTailEntry(baseEntry, resolvedDeal)) {
    return baseEntry.tail;
  }

  if (resolvedDeal) return resolvedDeal;

  return collectionEntry?.tail ?? baseEntry?.tail ?? null;
}
